import { IUserOperator } from "api/types/_user";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import TooltipCustom from "components/Common/TooltipCustom";
import { useRole } from "components/Hooks/UserHooks";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import TableContainer from "../../../components/Common/TableContainer";
import OperatorForm from "./OperatorForm";
import { formatDateStringToKorea } from "helpers/format";
import {
  LIST_OPERATOR_ROLE_OPTION,
} from "helpers/constans";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { ROUTES } from "routes/allRoutes";
import DropdownAsyncCompanies from "components/Common/DropdownAsyncCompanies";
import { deleteUserAdmin, getUserAdmins, putUserAdmin } from "store/admin/thunk";
import { useNavigate } from 'react-router-dom';

interface Option {
  label: string;
  value: string;
}

const TYPE_SELECT_DEFAULT: string = "created_at";

registerLocale("en", en);
registerLocale("ko", ko);

const UserList = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const navigate = useNavigate();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    sort_by: withDefault(StringParam, TYPE_SELECT_DEFAULT),
    name: withDefault(StringParam, ""),
    email: withDefault(StringParam, ""),
    company_id: withDefault(StringParam, ""),
    role_code: withDefault(StringParam, ""),
  });

  const [nameSearch, setNameSearch] = useState<string>(query?.name || "");
  const [emailSearch, setEmailSearch] = useState<string>(query?.email || "");

  const [roleSearch, setRoleSearch] = useState<any>(
    LIST_OPERATOR_ROLE_OPTION?.find((item) => item?.value === query?.role_code)
      ?.value || ""
  );

  const [companySearch, setCompanySearch] = useState<Option | any>(query?.company_id);

  const [userEdit, setUserEdit] = useState<
    (IUserOperator & { isCopy?: boolean }) | null
  >(null);

  const [isOpenConfirm, setIsConfirm] = useState<boolean>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [initialValuesDefault, setInitialValuesDefault] =
    useState<IUserOperator | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.Admin;

  const UserAdminProperties = createSelector(selectLayoutState, (state) => ({
    admins: state.admins,
    isAdminSuccess: state.isAdminSuccess,
    isAdminLoading: state.isAdminLoading,
    error: state.error,
  }));

  // Inside your component
  const { admins, isAdminLoading } = useSelector(UserAdminProperties);

  useEffect(() => {
    dispatch(getUserAdmins(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      email: emailSearch || "",
      name: nameSearch || "",
      role_code: roleSearch || "",
      company_id: (companySearch && companySearch?.value !== "all") ? companySearch?.value : "",
      sort_by: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        email: "",
        name: "",
        role_code: "",
        company_id: "",
        sort_by: TYPE_SELECT_DEFAULT,
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setEmailSearch("");
    setNameSearch("");
    setRoleSearch("");
    setCompanySearch({
      label: t("All"),
      value: "",
    });
  };

  // Begin::Update User
  const onCloseClick = () => {
    setUserEdit((_prev) => null);
    setIsOpen((_prev) => false);
  };

  const triggerRefresh = () => {
    onCloseClick();
    dispatch(getUserAdmins(query));
  };
  //End::Update User

  // Begin::Delete
  const handleConfirmDelete = (item: IUserOperator) => {
    setInitialValuesDefault((_prev) => item);
    setIsConfirm((_prev) => true);
  };

  const handleActionDelete = async () => {
    if (!initialValuesDefault?.id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await deleteUserAdmin(initialValuesDefault?.id);
      if (response?.statusCode === 200) {
        dispatch(getUserAdmins(query));
        setIsConfirmLoading((_prev) => false);
        setIsConfirm((_prev) => false);
        setInitialValuesDefault((_prev) => null);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseConfirmClick = () => {
    setInitialValuesDefault((_prev) => null);
    setIsConfirm((_prev) => false);
  };
  // End::Delete

  // Begin::Change status
  const handleOnConfirmUpdate = async (item: {
    id: number | string;
    status: number | boolean;
  }) => {
    try {
      if (!item) {
        return;
      }
      const response: any = await putUserAdmin(item?.id, {
        is_active: item?.status,
      });
      
      if (response?.statusCode === 200) {
        dispatch(getUserAdmins(query));
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };
  // End::Change status

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        accessor: "id",
        filterable: true,
        sortable: false,
        thWidth: 60,
        Cell: ({ ...props }) => {
          return (
            <div style={{ minWidth: "40px" }}>
              {admins?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Status"),
        accessor: "is_active",
        thWidth: 80,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={cell?.value === true}
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    id: item?.id,
                    status: !!value ? true : false,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          return (
            <>
              <div style={{ minWidth: "100px" }}>{item?.name || "-"}</div>
            </>
          );
        },
      },
      {
        Header: t("Email"),
        accessor: "username",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          return (
            <>
              <div style={{ minWidth: "100px" }}>{cell?.value}</div>
            </>
          );
        },
      },
      {
        Header: t("Role"),
        accessor: "role.code",
        filterable: true,
        sortable: false,
        Cell: (cell: any) => {
          return (
            <>
              <div style={{ minWidth: "100px" }}>
                {t(
                  LIST_OPERATOR_ROLE_OPTION?.find(
                    (item) => item?.value === cell?.value
                  )?.label || ""
                )}
              </div>
            </>
          );
        },
      },
      {
        Header: `${t("Company")}`,
        accessor: "company.name",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const company: any = cell?.row?.original?.company;
          return (
            company ? (
              <div>{company?.name}</div>
            ) : "-"
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "created_at",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 100,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "70px" }}
            >
              {isHavePermissionRole(
                ROLES_FOR_APP.OPERATOR_UPDATE,
                userPermissions
              ) && (
                <TooltipCustom
                  title={t("Button Update Operator")}
                  id={`update-us-${item?.id}`}
                >
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setUserEdit((prev) => item);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom"></i>
                  </Link>
                </TooltipCustom>
              )}
              {isHavePermissionRole(
                ROLES_FOR_APP.OPERATOR_DELETE,
                userPermissions
              ) && (
                <TooltipCustom
                  title={t("Button Delete Operator")}
                  id={`detail-cp-${item?.id}`}
                >
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleConfirmDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </TooltipCustom>
              )}
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, query, admins]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userPermissions === "COMPANY" && !isHavePermissionRole([ROLES_FOR_APP.USER_MANAGEMENT], userPermissions)) {
      navigate(ROUTES.APPLICATION_MANAGEMENT, { replace: true });
    }
  }, [userPermissions, navigate]);

  useEffect(() => {
    document.title = `${t("Operator Management")} | ShowStar`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Operator Management")} pageTitle={t("Home")} />
          <Row>
            {isHavePermissionRole(
              ROLES_FOR_APP.OPERATOR_LIST,
              userPermissions
            ) && (
              <Col lg={12}>
                <Card
                  id="operatorList"
                  style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
                >
                  <CardHeader className="border-0">
                    <Row className="g-4 align-items-center">
                      <div className="col-5 con-sm-6">
                        <div>
                          <h5 className="card-title mb-0">
                            <span className="me-2">{t("Total")}:</span>
                            <CountUp
                              start={0}
                              end={admins?.pagination?.total || 0}
                              duration={1}
                              className="text-primary"
                            />
                          </h5>
                        </div>
                      </div>
                      <div className="col-7 con-sm-6 text-end">
                        {isHavePermissionRole(
                          ROLES_FOR_APP.OPERATOR_CREATE,
                          userPermissions
                        ) && (
                          <div className="col-sm-auto">
                            <div>
                              <button
                                type="button"
                                className="btn btn-success add-btn"
                                // className="btn btn-success add-btn rounded-pill"
                                id="create-btn"
                                onClick={() => setIsOpen((_prev) => true)}
                              >
                                <i className="ri-add-line align-bottom me-1"></i>
                                {t("Button Create Operator")}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </Row>
                  </CardHeader>
                  <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                    <Row className="g-4 align-items-center mt-0">
                      <Col
                        sm={6}
                        md={6}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("Name")}`}
                          isShow={true}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Name")}...`}
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={6}
                        md={6}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("Email")}`}
                          isShow={true}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email")}...`}
                            value={emailSearch}
                            onChange={(e) => setEmailSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Role")} isShow={true}>
                          <DropdownOptionNew
                            name="role-search"
                            onChangeSelect={(event: any) =>
                              setRoleSearch(event?.value)
                            }
                            initialValue={roleSearch}
                            dataList={LIST_OPERATOR_ROLE_OPTION}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            placeholder={`${t("Select Role")}...`}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            hideSelectedOptions={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper label={t("Company")} isShow={true}>
                          <DropdownAsyncCompanies
                            name="company_id"
                            onChangeSelect={(event: any) => {
                              setCompanySearch(event);
                            }}
                            isMulti={false}
                            placeholder={`${t("Select Company")}...`}
                            isShowIconCheck={false}
                            listCheckedUsers={companySearch}
                            isAllOption={true}
                            isClearable={false}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={12}
                        xl={12}
                        xxl={12}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-3 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-2 "
                            onClick={searchData}
                            disabled={isAdminLoading}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <CardBody className="pt-3">
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={admins?.items?.length ? admins?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={admins?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(admins?.pagination?.total) /
                          Number(admins?.pagination?.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: query.sort_by,
                        order_by: query.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isAdminLoading}
                    />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
        <ToastContainer closeButton={false} limit={1} />

        <Modal
          isOpen={!!userEdit?.id || isOpen}
          centered={true}
          size="lg"
          scrollable={true}
        >
          <ModalHeader toggle={onCloseClick}>
            <>
              {!!userEdit?.id
                ? t("Button Update Operator")
                : t("Button Create Operator")}
            </>{" "}
            <>{userEdit?.id && (" - " + (userEdit?.name || "") || "")}</>
          </ModalHeader>
          <ModalBody>
            <div style={{ minHeight: "calc(100dvh - 300px)" }}>
              <OperatorForm
                isModal={true}
                item={userEdit}
                isCopy={!!userEdit?.isCopy}
                triggerRefresh={triggerRefresh}
              />
            </div>
          </ModalBody>
        </Modal>

        <ModalConfirm
          header={t("Button Delete Operator")}
          isOpen={isOpenConfirm}
          isLoading={isConfirmLoading}
          onClose={onCloseConfirmClick}
          onConfirm={handleActionDelete}
        />
      </div>
    </React.Fragment>
  );
};

export default UserList;
